import {useEffect, useRef, useState} from 'react';

export const renewalVersion = metadata => metadata?.generation_id && metadata.forecast_enabled !== false
    // The API uses strings; save/status caches also contain numeric revisions.
    ? [metadata.generation_id, metadata.matching_revision == null ? null : String(metadata.matching_revision), metadata.input_fingerprint ?? null]
    : null;

export const accountRenewalMetadata = (dataStatus, accountId) => {
    const metadata = dataStatus?.renewal_metadata;
    return metadata?.customer_id === accountId ? metadata : null;
};

export const resultRenewalMetadata = data => data?.renewal_metadata || data?.metadata?.renewal_metadata;
export const isRenewalUpdatingError = error =>
    ['source_stale', 'generation_required'].includes(error?.response?.data?.code);

export const renewalUpdateNotice = (stale, metadata, forecast = false, hasResult = true) => {
    if (forecast && metadata?.forecast_enabled === false) return null;
    const subject = forecast ? 'forecast' : 'renewal data';
    if (hasResult && !stale) return null;
    if (!hasResult) {
        if (metadata?.build_failed) return `The data build failed. The ${subject} will be available after a successful build.`;
        if (metadata?.build_in_progress) return `Data is rebuilding. The ${subject} will appear automatically when it is ready.`;
        if (stale) return `The ${subject} is waiting for the next scheduled data build.`;
        return null;
    }
    if (metadata?.build_failed) return `The data build failed. Showing the last complete ${subject}. Results will update automatically after a successful build.`;
    if (metadata?.build_in_progress) return `Data is rebuilding. Showing the last complete ${subject}; results will update automatically.`;
    if (stale) return `New CRM changes will appear after the next scheduled data build. Showing the last complete ${subject}; results will update automatically.`;
    return null;
};

export const validateRenewalResult = (data, expected) => {
    const actual = resultRenewalMetadata(data);
    if (actual?.snapshot_only) return data;
    if (actual?.customer_id && expected?.customer_id && actual.customer_id !== expected.customer_id) {
        const error = new Error('Forecast response belongs to a different account.');
        error.response = {status: 403};
        throw error;
    }
    // Status describes the latest observed build, not the inputs used by this
    // successful calculation. Component compatibility is checked together below.
    return data;
};

/** Retain one complete set; never merge independently refreshed generations. */
export function useCompatibleRenewalResults(scope, values, expected, recovery = {}) {
    const saved = useRef(null);
    const scopeKey = JSON.stringify(scope);
    const previous = saved.current?.scope === scopeKey ? saved.current.values : null;
    const versions = values.map(value => resultRenewalMetadata(value)?.snapshot_only ? 'snapshot'
        : JSON.stringify(renewalVersion(resultRenewalMetadata(value))));
    const complete = values.every(Boolean) && versions.every(version => version === versions[0]);
    const actual = resultRenewalMetadata(values[0]);
    const previousMetadata = resultRenewalMetadata(previous?.[0]);
    const olderThan = metadata => metadata?.matching_revision != null && actual?.matching_revision != null
        && Number.isFinite(Number(metadata.matching_revision)) && Number.isFinite(Number(actual.matching_revision))
        && Number(actual.matching_revision) < Number(metadata.matching_revision);
    const legacy = !renewalVersion(expected) && versions.every(version => version === 'null');
    const verified = complete && (versions[0] === 'snapshot' || legacy || (versions[0] !== 'null' && !olderThan(previousMetadata)));
    const retained = previous || (complete && versions[0] !== 'null' ? values : null);
    const displayed = verified ? values : retained;
    useEffect(() => {
        if (displayed) saved.current = {scope: scopeKey, values: displayed};
        else if (saved.current?.scope !== scopeKey) saved.current = null;
    }, [scopeKey, displayed]);
    const mismatch = values.every(Boolean) && !verified;
    const resetKey = JSON.stringify([scopeKey, renewalVersion(expected), recovery.streamingUpdate]);
    const [retryState, setRetryState] = useState({key: resetKey, attempts: 0, running: false});
    const attempts = retryState.key === resetKey ? retryState.attempts : 0;
    const running = retryState.key === resetKey && retryState.running;
    const refresh = useRef(recovery.refresh);
    refresh.current = recovery.refresh;
    useEffect(() => {
        if (verified && attempts && !running) setRetryState({key: resetKey, attempts: 0, running: false});
    }, [verified, attempts, running, resetKey]);
    useEffect(() => {
        if (!mismatch || recovery.fetching || recovery.error || running || attempts >= 2 || !refresh.current) return;
        // Independent API reads can straddle one CRM update. Retry their
        // existing queries together, with a bound; never synthesize HTTP 503.
        const timer = setTimeout(() => {
            setRetryState({key: resetKey, attempts: attempts + 1, running: true});
            Promise.resolve().then(() => refresh.current()).catch(() => {})
                .finally(() => setRetryState(state => state.key === resetKey ? {...state, running: false} : state));
        }, 500);
        return () => clearTimeout(timer);
    }, [mismatch, recovery.fetching, recovery.error, running, attempts, resetKey]);
    const consistencyError = mismatch && !recovery.fetching && !running && attempts >= 2
        ? {message: 'Forecast inputs changed while loading. Retry to load a consistent result.', code: 'RENEWAL_COMPONENT_MISMATCH'} : null;
    return {values: displayed || values.map(() => undefined), snapshotOnly: complete && versions[0] === 'snapshot',
        consistencyError,
        updating: !verified || (versions[0] !== 'snapshot' && expected?.forecast_enabled !== false && olderThan(expected))};
}
